import type { ProfileCategory } from '@shared/constants/profile-categories';

import type { HnwisLinkFilters } from '@/shared/routes/hnwis-link';

/**
 * The dashboard's cross-filter: what the user clicked (a type, a country, a
 * month) narrows every card at once. Clicking the same value again lets it go.
 */
export interface DashboardScope {
  /** A category, the Institutionals types together (#629), or the remainder. */
  type?: ProfileCategory | 'institutionals' | 'other';
  country?: string;
  /** `YYYY-MM`: LPs Invyo first added that month. */
  month?: string;
}

export type ScopeKey = keyof DashboardScope;

/** The scope with `key` set to `value`, or cleared when it already was `value`. */
export function toggleScope<K extends ScopeKey>(
  scope: DashboardScope,
  key: K,
  value: NonNullable<DashboardScope[K]>,
): DashboardScope {
  const next = { ...scope };
  if (next[key] === value) delete next[key];
  else next[key] = value;
  return next;
}

export const isScoped = (scope: DashboardScope) =>
  scope.type !== undefined || scope.country !== undefined || scope.month !== undefined;

/** `?type=…&country=…&month=…` for the dashboard endpoints ('' when unscoped). */
export function scopeQuery(scope: DashboardScope): string {
  const params = new URLSearchParams();
  if (scope.type) params.set('type', scope.type);
  if (scope.country) params.set('country', scope.country);
  if (scope.month) params.set('month', scope.month);
  const q = params.toString();
  return q ? `?${q}` : '';
}

/** The HNWIs filters that open exactly the rows the scoped figures count. */
export function scopeLink(scope: DashboardScope): HnwisLinkFilters {
  return {
    ...(scope.type ? { type: scope.type } : {}),
    ...(scope.country ? { country: scope.country } : {}),
    ...(scope.month ? { addedByInvyoIn: scope.month } : {}),
  };
}
